import { useRef, useState } from 'react';
import { Camera, ImageUp, RotateCcw, Trash2, UserRound } from 'lucide-react';
import { tr } from '@/lib/i18n-core';
import { Button, Field } from '@/components/ui/primitives';
import { PatientAvatar } from '@/components/PatientAvatar';
import { KEEP_PHOTO, PHOTO_PICK_TYPES, downscalePhoto, draftPreview, photoProblemText, type PhotoDraft } from './patient-photo';

function prefersCamera(): boolean {
  return typeof window !== 'undefined' && typeof window.matchMedia === 'function' && window.matchMedia('(pointer: coarse)').matches;
}

export function PatientPhotoField({
  value,
  onChange,
  currentKey,
  name,
}: {
  value: PhotoDraft;
  onChange: (draft: PhotoDraft) => void;
  currentKey: string | null;
  name: string;
}) {
  const camera = useRef<HTMLInputElement>(null);
  const picker = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const [withCamera] = useState(prefersCamera);
  const previewUrl = draftPreview(value);

  const take = async (file: File | undefined) => {
    if (!file) return;
    setProblem(null);
    setBusy(true);
    try {
      const blob = await downscalePhoto(file);
      onChange({ kind: 'new', blob, previewUrl: URL.createObjectURL(blob) });
    } catch (error) {
      setProblem(photoProblemText(error));
    } finally {
      setBusy(false);
    }
  };

  const hasPhoto = value.kind === 'new' || (value.kind === 'keep' && Boolean(currentKey));
  const removed = value.kind === 'remove';

  return (
    <Field label={tr('opd', 'photo')} hint={tr('opd', 'photo_hint')} error={problem ?? undefined} className="sm:col-span-2">
      <div className="flex flex-wrap items-center gap-4">
        <PatientAvatar
          name={name}
          photoKey={value.kind === 'keep' ? currentKey : null}
          src={previewUrl}
          className="grid h-20 w-20 shrink-0 place-items-center rounded-2xl border bg-muted text-muted-foreground"
          fallback={<UserRound className="h-8 w-8" />}
        />
        <div className="flex flex-wrap items-center gap-2">
          <input
            ref={camera}
            type="file"
            accept="image/*"
            capture="user"
            className="hidden"
            onChange={(event) => {
              void take(event.target.files?.[0]);
              event.target.value = '';
            }}
          />
          <input
            ref={picker}
            type="file"
            accept={PHOTO_PICK_TYPES}
            className="hidden"
            onChange={(event) => {
              void take(event.target.files?.[0]);
              event.target.value = '';
            }}
          />
          {withCamera && (
            <Button type="button" variant="outline" size="sm" loading={busy} onClick={() => camera.current?.click()}>
              <Camera className="h-4 w-4" aria-hidden />
              {tr('opd', 'photo_take')}
            </Button>
          )}
          <Button type="button" variant="outline" size="sm" loading={busy && !withCamera} onClick={() => picker.current?.click()}>
            <ImageUp className="h-4 w-4" aria-hidden />
            {hasPhoto ? tr('opd', 'photo_replace') : tr('opd', 'photo_choose')}
          </Button>
          {hasPhoto && (
            <Button type="button" variant="ghost" size="sm" onClick={() => onChange(currentKey ? { kind: 'remove' } : KEEP_PHOTO)}>
              <Trash2 className="h-4 w-4" aria-hidden />
              {tr('opd', 'photo_remove')}
            </Button>
          )}
          {removed && (
            <Button type="button" variant="ghost" size="sm" onClick={() => onChange(KEEP_PHOTO)}>
              <RotateCcw className="h-4 w-4" aria-hidden />
              {tr('opd', 'photo_keep')}
            </Button>
          )}
        </div>
      </div>
    </Field>
  );
}
